/**
 * Pseudonymous Amplitude user IDs for the Experiment SDK.
 *
 * Analytics events go through the consent proxy (OCPT-816), which hashes
 * `user_id` before forwarding to Amplitude. The Experiment SDK calls
 * Amplitude directly, so we must apply the same Unified User Hashing here
 * to keep assignments and behaviour events on one profile.
 */

const usesProxyHashing = (): boolean =>
  Boolean(import.meta.env.VITE_AMPLITUDE_PROXY_BASE_URL)

const formatDigestAsUuid = (digest: ArrayBuffer): string => {
  const bytes = new Uint8Array(digest).slice(0, 16)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`
}

const hashRawUserId = async (rawUserId: string): Promise<string> => {
  const normalized = rawUserId.trim().toLowerCase()
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(normalized),
  )
  return formatDigestAsUuid(digest)
}

/**
 * Returns the Amplitude `user_id` to use for Experiment fetch/assignment calls.
 * When the consent proxy is configured, returns the hashed pseudonymous ID;
 * otherwise returns the raw UUID (local dev without proxy).
 */
const getHashedUserId = async (rawUserId: string): Promise<string> => {
  if (!usesProxyHashing()) return rawUserId
  return hashRawUserId(rawUserId)
}

export default { getHashedUserId, usesProxyHashing }
